<template>
    <div v-if="city" class="tfw-root" :style="bgStyle">
        <div class="tfw-content">
            <div class="tfw-top-row">
                <div class="tfw-square-tile" :class="{'tfw-square-tile--disabled': isLoading}" @click="!isLoading && callToCity(city.id)">
                    <div class="tfw-back-badge-img" :style="cityImgStyle"/>
                    <div class="tfw-square-label">{{ $t('common.backToCity') }}</div>
                </div>

                <div class="tfw-square-tile" @click="showObstacleInfo = true">
                    <div class="tfw-knowegle-frame">
                        <div v-if="city.stadion.stadionTrainer.img" class="tfw-knowegle-img" :style="knowegleImgStyle"/>
                    </div>
                    <div class="tfw-square-label">{{ $t('journey.trainerFightWin.knowegleLabel') }}</div>
                </div>
            </div>

            <div class="tfw-congrats-card" :class="$vuetify.theme.dark ? 'transparent03--dark white--text' : 'transparent03 black--text'">
                <div class="tfw-badge-block">
                    <div class="tfw-congrats-title">{{ $t('common.congratulations') }}</div>
                    <div class="tfw-congrats-subtitle">{{ $t('journey.trainerFightWin.congratsSubtitle', {badge: $t('common.badgeNames.' + city.stadion.badge.name)}) }}</div>
                    <div class="tfw-badge-img-wrap">
                        <v-img
                            :src="require(`@/assets/pic/badge/${city.stadion.id}.png`)"
                            class="tfw-badge-img"
                            height="100%"
                            contain
                        />
                    </div>
                </div>
                <div class="tfw-toplist-scroll">
                    <ToplistTable
                        :toplist="toplist"
                        :headers="[$t('journey.trainerFightWin.headerTrainer'), $t('journey.trainerFightWin.headerDay'), $t('journey.trainerFightWin.headerRound'), $t('journey.trainerFightWin.headerDate')]"
                        :keys="['trainer','day','round','created']"
                    />
                </div>
            </div>
        </div>

        <ResponsiveDialog v-model="showObstacleInfo" :max-width="480" :header-title="$t('journey.trainerFightWin.obstacleDialogTitle')" header-class="skin-window-header--obstacleinfo" card-class="skin-card">
            <div class="tfw-obstacle-dialog skin-window--obstacleinfo">
                <div class="tfw-obstacle-content">
                    <div
                        v-for="obstacle in obstacleInfo" :key="obstacle.name"
                        class="tfw-obstacle-item"
                    >
                        <div class="tfw-obstacle-name">{{ obstacle.name }}</div>
                        <v-img
                            :src="require(`@/assets/pic/obstacles/`+obstacle.img)"
                            height="220px"
                            cover
                            class="tfw-obstacle-img"
                        />
                        <div class="tfw-obstacle-skills">
                            <v-tooltip
                                top
                                v-for="skill in obstacle.travellingSkills"
                                :key="skill.id"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <TypePill
                                        v-bind="attrs"
                                        v-on="on"
                                        :type="skill.type"
                                        class="ma-1 font-weight-regular"
                                    >
                                        {{ skill.name }}
                                    </TypePill>
                                </template>
                                <table>
                                    <tr>
                                        <td class="pr-1">{{ $t('journey.trainerFightWin.typeLabel') }} </td>
                                        <td>{{ skill.type.id }}</td>
                                    </tr>
                                    <tr>
                                        <td class="pr-1">{{ $t('journey.trainerFightWin.dmgLabel') }} </td>
                                        <td> {{ skill.formula }}</td>
                                    </tr>
                                    <tr v-if="skill.effect">
                                        <td class="pr-1">{{ $t('journey.trainerFightWin.dmgLabel') }} </td>
                                        <td> {{ skill.effect }}</td>
                                    </tr>
                                    <tr>
                                        <td class="pr-1">{{ $t('journey.trainerFightWin.lvlLabel') }} </td>
                                        <td> {{ skill.lvl }}</td>
                                    </tr>
                                    <tr>
                                        <td class="pr-1">{{ $t('journey.trainerFightWin.costLabel') }} </td>
                                        <td> {{ skill.cost }} {{ $t('journey.trainerFightWin.expSuffix') }}</td>
                                    </tr>
                                </table>
                            </v-tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </ResponsiveDialog>
    </div>
</template>

<script>
    // Win obrazovka po výhře nad trenérem stadionu - malý odznak "back to city"
    // (stejný vzor jako wild-next-back-badge z WildNext.vue), Gratulace+odznak
    // karta jako vizuální centrum, klikací portrét trenéra otevírá popup s
    // nápovědou na překážky, žebříček sám o sobě scrollovatelný. Design viz
    // claude/components/journey-dungeon.md.
    import {mapActions, mapGetters} from 'vuex';
    import * as journeyApi from '@/api/journeyApi';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import ToplistTable from '@/components/journey/positions/pieces/ToplistTable';
    import TypePill from '@/components/utils/TypePill';

    export default {
        name: 'TrainerFightWinNext',
        components: {ResponsiveDialog, ToplistTable, TypePill},
        props: {
            trainerPosition: {type: Object, default: null},
        },
        data() {
            return {
                toplist: [],
                showObstacleInfo: false,
                obstacleInfo: [],
                loading: false,
            }
        },
        computed: {
            ...mapGetters({
                user: 'user/item',
                city: 'city/item',
                isLoading: 'journey/isLoading',
            }),
            bgStyle() {
                const pic = this.city ? this.city.stadion.badge.id + '.png' : 'gym_default.png';
                return {backgroundImage: `url(${require('@/assets/pic/stadion/gyms/' + pic)})`};
            },
            cityImgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/city/' + this.city.id + '.png')})`};
            },
            knowegleImgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/trainer/' + this.city.stadion.stadionTrainer.img)})`};
            },
        },
        watch: {
            trainerPosition() {
                this.loadCityAndData();
            }
        },
        created() {
            this.loadCityAndData();
            this.trainerInfoMessages(this.user.id);
        },
        methods: {
            ...mapActions({
                fetchData: 'journey/fetchObject',
                positionPost: 'journey/positionPost',
                getCity: 'city/loadItem',
                trainerInfoMessages: 'trainer/loadInfoMessages',
            }),
            loadCityAndData() {
                this.getCity(this.trainerPosition.city.id).then(() => {
                    this.getToplist();
                    this.loadObstacleInfo();
                });
            },
            loadObstacleInfo() {
                this.loading = true;
                this.fetchData(journeyApi.getTrainerObstacleHint(this.city.stadion.stadionTrainer.id)).then((data) => {
                    this.obstacleInfo = data;
                    this.loading = false;
                });
            },
            callToCity(cityId) {
                this.positionPost(journeyApi.travel(this.trainerPosition.id, cityId));
            },
            getToplist() {
                this.fetchData(journeyApi.badgeToplist(this.city.stadion.badge.id)).then((data) => {
                    this.toplist = data['member'];
                });
            },
        },
    }
</script>

<style scoped>
.tfw-root {
    position: fixed;
    inset: 0;
    height: 100%;
    background-size: cover;
    background-position: center;
    overflow-y: auto;
}
/* Back-to-city a Knowegle ve stejném řádku, dvousloupcový grid, oba čtverečky
   stejně velké - v normálním flow obsahu, ne absolutně přes pozadí. */
/* Výškový rozpočet:
   - .tfw-top-row: základní nárok 25%, smršťuje se přednostně (flex-shrink:3)
     až na min-height:20% podlahu.
   - .tfw-congrats-card: jedna karta pro congrats+odznak+žebříček (žebříček tak
     dostane víc místa, není oddělený nadpisem "Glory list"). Roste na všechno
     místo po top-row (flex: 1 1 auto).
   - .tfw-badge-img-wrap: uvnitř karty pevná výška, hlavní roli hraje žebříček.
   - .tfw-toplist-scroll: flex: 1 1 auto - dostane všechno zbývající místo v
     kartě, sám scrolluje (Z7 princip). */
.tfw-top-row {
    width: 100%;
    /* stejná max-width jako karty pod ním (Congratulations/Glory list) */
    max-width: 420px;
    /* Pevný flex-basis 25% je jediný zdroj pravdy pro výšku řádku - čtverečky
       uvnitř výšku nepočítají samy (viz .tfw-back-badge-img níže), jen zdědí
       100% téhle výšky. Nespoléhat na "auto" (obsahová velikost) ani na
       width vypočtený z viewportu (25vh) - dvě nezávislé reference si
       neodpovídaly a čtverečky byly useknuté řádkem. */
    flex: 0 3 25%;
    min-height: 20%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    justify-items: stretch;
}
.tfw-square-tile {
    width: 100%;
    /* vyplni celou (uz korektni) vysku radku - viz komentar u .tfw-top-row */
    height: 100%;
    /* container-query kontext pro popisek pod ctvercem (viz .tfw-square-label
       nize) - stejny princip jako .circle-tile-avatar-wrap v NextCircleTile.vue */
    container-type: inline-size;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.tfw-square-tile--disabled {
    pointer-events: none;
    opacity: 0.6;
}
.tfw-back-badge-img {
    /* flex:1 (ne height:100%) - .tfw-square-tile je flex sloupec (obrázek +
       .tfw-square-label pod ním), obrázek smí vyplnit jen zbývající výšku po
       popisku; z výsledné (flexem dopočtené) výšky pak aspect-ratio:1 správně
       dopočítá šířku. max-width:100% jako pojistka pro landscape případy. */
    flex: 1 1 auto;
    min-height: 0;
    width: auto;
    max-width: 100%;
    aspect-ratio: 1;
    border-radius: 14px;
    background-size: cover;
    background-position: center;
    border: 5px solid #C0C0C0;
    box-shadow: 0 3px 8px rgba(0,0,0,0.5);
    box-sizing: border-box;
}
.tfw-square-label {
    margin-top: -14px;
    position: relative;
    z-index: 1;
    background: rgba(0,0,0,0.65);
    color: #fff;
    /* škáluje podle šířky čtverečku (cqw, container-type na .tfw-square-tile výše),
       stejný princip jako .circle-tile-title */
    font-size: clamp(15px, 15cqw, 30px);
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
    white-space: nowrap;
}
.tfw-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    /* poslední číslo (bottom) = rezerva pro globální TeamBottomSheet (App.vue),
       54px NE 22px jako .app-screen-bg - .tfw-root je fixed/inset:0, počítá
       se proti skutečnému viewportu (bez vh-fix marginu screenů pod .v-main) */
    padding: 74px 16px 54px 16px;
    height: 100%;
    box-sizing: border-box;
}
/* Gratulace + odznak = vizuální centrum obrazovky, poloprůsvitná karta
   ("transparent03"). Výškový rozpočet viz komentář u .tfw-top-row výše. */
.tfw-congrats-card {
    width: 100%;
    max-width: 420px;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-radius: 18px;
    padding: 6px 16px 10px 16px;
    text-align: center;
    box-shadow: 2px 3px 10px rgba(0,0,0,0.35);
    box-sizing: border-box;
}
/* titulek/podtitulek stohované NAD odznakem (ne přes obrázek), ale odznak+text
   pořád dohromady tvoří jeden 50% blok spolu s tabulkou (viz šablona výše) */
.tfw-badge-block {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.tfw-congrats-title {
    flex: none;
    font-size: 26px;
    font-weight: 800;
}
.tfw-congrats-subtitle {
    flex: none;
    font-size: 18px;
    font-weight: 600;
    margin-top: 2px;
}
.tfw-badge-img-wrap {
    flex: 1 1 auto;
    min-height: 0;
    margin-top: 6px;
}
.tfw-badge-img {
    height: 100%;
    width: 100%;
}
.tfw-knowegle-frame {
    flex: 1 1 auto;
    min-height: 0;
    width: auto;
    max-width: 100%;
    aspect-ratio: 1;
    border-radius: 14px;
    padding: 5px;
    box-sizing: border-box;
    /* barva natvrdo, ne var(--v-victoryGold-base) - Vuetify 2 generuje
       --v-{barva}-base proměnné jen s theme.options.customProperties:true
       (vuetify.js to nemá zapnuté), neplatný var() uvnitř linear-gradient()
       shodí celé pravidlo (neplatný color-stop), gradient by se nevykreslil */
    background: linear-gradient(160deg, #FFECB3 0%, #FFD700 60%, #FF8F00 100%);
    box-shadow: 0 0 14px 4px rgba(255,193,7,0.85), 0 3px 8px rgba(0,0,0,0.5);
}
/* jen horní polovina obrázku trenéra - <v-img>/object-fit neumí ořez na
   půlku, takže background-image trik: výška 200% (obrázek 2x vyšší než
   rámeček) + background-position:top ukáže jen horní polovinu beze změny poměru stran */
.tfw-knowegle-img {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    background-size: auto 200%;
    background-position: top center;
    background-repeat: no-repeat;
}
/* žebříček roste na všechno zbývající místo v kartě, sám scrolluje (Z7 princip);
   font-size dědí default ToplistTable.vue (bez vlastního zvětšení), jinak se
   Date sloupec zabaluje na víc řádků */
.tfw-toplist-scroll {
    flex: 1 1 0;
    min-height: 0;
    margin-top: 8px;
    overflow-y: auto;
}
/* Round sloupec (na rozdíl od TrainerWildFightWinNext.vue) zabírá místo navíc,
   které by se jinak hodilo datu - pod 440px se schová, ať má Date dost prostoru */
@media (max-width: 440px) {
    .tfw-toplist-scroll ::v-deep th:nth-child(3),
    .tfw-toplist-scroll ::v-deep td:nth-child(3) {
        display: none;
    }
}
/* pozadí řídí skin (public/skins/normal.css, .skin-window--obstacleinfo) -
   v zaoblených rozích karty může prosvítat tenký proužek Vuetify defaultní barvy */
.tfw-obstacle-dialog {
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.tfw-obstacle-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    /* rezerva pro plovoucí zavírací křížek (ResponsiveDialog) - stejná hodnota jako ostatní next/ popupy */
    padding: 16px 16px var(--responsive-dialog-close-clearance) 16px;
}
.tfw-obstacle-item {
    margin-bottom: 16px;
}
.tfw-obstacle-name {
    text-align: center;
    font-weight: 700;
    font-size: 16px;
}
.tfw-obstacle-img {
    margin-top: 6px;
    border-radius: 8px;
}
.tfw-obstacle-skills {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 8px;
}
</style>
